import { rubberBand, velocityOf } from '@lunary/app-core/swipe-physics'
import type { Sample } from '@lunary/app-core/swipe-physics'

type Options = {
  panel: HTMLElement
  scrim: HTMLElement
  isHandle: (target: EventTarget | null) => boolean
  canDrag: () => boolean
  onDismiss: () => void
}

const CLOSE_PART = 0.25
const FLICK = 0.4
const MIN_FLICK_PX = 20

export function attachSheetDrag({ panel, scrim, isHandle, canDrag, onDismiss }: Options): () => void {
  let drag: { id: number; startY: number; samples: Sample[]; height: number; dy: number } | null = null

  const reset = () => {
    panel.style.transition = ''
    scrim.style.transition = ''
    panel.style.transform = ''
    scrim.style.opacity = ''
  }

  const onDown = (e: PointerEvent) => {
    if (drag || e.button !== 0 || !canDrag() || !isHandle(e.target)) return
    const target = e.target
    if (target instanceof Element && target.closest('button, a, input, textarea, select, [role="combobox"]')) return
    drag = { id: e.pointerId, startY: e.clientY, samples: [{ t: e.timeStamp, x: e.clientY }], height: panel.offsetHeight, dy: 0 }
    panel.setPointerCapture(e.pointerId)
    panel.style.transition = 'none'
    scrim.style.transition = 'none'
  }

  const onMove = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return
    const raw = e.clientY - drag.startY
    drag.samples.push({ t: e.timeStamp, x: e.clientY })
    if (drag.samples.length > 12) drag.samples.shift()
    drag.dy = raw >= 0 ? raw : rubberBand(raw, drag.height)
    panel.style.transform = `translateY(${drag.dy}px)`
    scrim.style.opacity = String(Math.max(0, 1 - Math.max(0, drag.dy) / drag.height))
  }

  const onUp = (e: PointerEvent) => {
    if (!drag || e.pointerId !== drag.id) return
    const { dy, height, samples } = drag
    drag = null
    const velocity = velocityOf(samples)
    const go = dy > height * CLOSE_PART || (velocity > FLICK && dy > MIN_FLICK_PX)
    if (go) {
      panel.style.transition = ''
      scrim.style.transition = ''
      onDismiss()
      return
    }
    reset()
    if (dy !== 0 && typeof panel.animate === 'function') {
      panel.animate([{ transform: `translateY(${dy}px)` }, { transform: 'none' }], { duration: 320, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1)' })
      scrim.animate([{ opacity: String(Math.max(0, 1 - Math.max(0, dy) / height)) }, { opacity: '1' }], { duration: 240, easing: 'ease-out' })
    }
  }

  panel.addEventListener('pointerdown', onDown)
  panel.addEventListener('pointermove', onMove)
  panel.addEventListener('pointerup', onUp)
  panel.addEventListener('pointercancel', onUp)
  return () => {
    panel.removeEventListener('pointerdown', onDown)
    panel.removeEventListener('pointermove', onMove)
    panel.removeEventListener('pointerup', onUp)
    panel.removeEventListener('pointercancel', onUp)
    if (drag) reset()
    drag = null
  }
}

export const clearDragStyles = (panel: HTMLElement | null, scrim: HTMLElement | null) => {
  if (panel) panel.style.transform = ''
  if (scrim) scrim.style.opacity = ''
}
